import {
  AbsenceType,
  CallIn,
  Exchange,
  Person,
  Team,
  TeamDuty,
  TeamShift,
  TeamShiftDef,
} from '@pdcplan/shared/api/TeamPlan';
import {
  Button,
  IconEnum,
  IconSize,
  Icons,
  colors,
} from '@pdcfrontendui/components';
import {
  getShiftStatusText,
  shiftIsDeleteable,
  shiftIsEditable,
  shiftRequiresAction,
  shouldShowRegisterAbsence,
} from '../util/shifts';

import { Duties } from './Duties';
import { EmployeeMap } from '../api/model';
import React from 'react';
import RegisterAbsenceSection from './Sections/RegisterAbsenceSection';
import { ExchangeTypeEnum, TeamShiftStatusEnum } from '@pdcplan/shared/enums';
import classNames from 'classnames';
import colorVarMap from '@pdcfrontendui/components/colors/colors';
import { currentLanguage } from '../currentLanguage';
import * as dateFns from 'date-fns';
import {
  formatActivityOrDutylineTime,
  isSameDayMidnightInclusive,
} from '../util/dates';
import ids from '../testing/ids';
import scss from './ShiftCard.module.scss';
import {
  ShiftActionButton,
  ShiftActionButtonVariant,
} from './ShiftActionButton';
import { useId } from '@pdcfrontendui/hooks';
import { useFormatter } from '../hooks/useLang';
import {
  isCreatedShift,
  isEditedShift,
  ShiftDraft,
  teamShiftToEditedShift,
} from '../ListView/CreateShift/EditedShift';
import { ActiveOfferView } from './ShiftViewEnums';
import { ActionRequiredCard } from './ActionRequiredCard';

export function ShiftInfoEntry({
  label,
  children,
  className,
  valueClassName,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
  valueClassName?: string;
}) {
  const id = useId();
  return (
    <li className={classNames(scss.entry, className)}>
      <div id={id} className={scss.label}>
        {label}
      </div>
      <div className={valueClassName} aria-labelledby={id}>
        {children}
      </div>
    </li>
  );
}

export function ShiftInfo({
  shift,
  isAssigningShift,
  absenceLabel,
  dutyLines,
  children,
  substitute,
  onRemoveShift,
  openEditShift,
  shiftDraft,
  attemptEndAbsence,
  formerAssignee,
  currentEmployee,
  hideStatus,
  testId,
  swapDetails,
  offeredTo,
  shiftLabelOverride,
}: {
  shift: TeamShift;
  isAssigningShift?: boolean;
  absenceLabel?: string;
  dutyLines: TeamDuty[];
  children?: React.ReactNode;
  substitute?: CallIn;
  onRemoveShift?: () => void;
  isCreatedShift?: boolean;
  currentEmployee?: Person;
  formerAssignee?: Person;
  hideStatus?: boolean;
  attemptEndAbsence?: () => void;
  openEditShift?: () => void;
  shiftDraft: ShiftDraft | null;
  testId?: string;
  swapDetails: Exchange | null;
  offeredTo?: string[];
  shiftLabelOverride?: string;
}) {
  const formatter = useFormatter();
  const editable =
    !isAssigningShift && shiftIsEditable(shift) && !!openEditShift;
  const deleteable =
    !isAssigningShift &&
    !editable &&
    shiftIsDeleteable(shift) &&
    !!onRemoveShift;
  const actionRequired = shiftRequiresAction(shift);
  const overlaps = !isSameDayMidnightInclusive(
    shift.period.from,
    shift.period.to
  );
  return (
    <div
      className={classNames(
        scss.info,
        absenceLabel === undefined && actionRequired && scss.actionRequired,
        absenceLabel !== undefined && scss.absent,
        isAssigningShift && scss.assigning
      )}
      data-testid={testId}
    >
      <div className={scss.header}>
        <div className={scss.left}>
          <div className={scss.time}>
            <div>{formatter.time(shift.period.from, false)}</div>
            <div className={overlaps ? scss.overlap : undefined}>
              {overlaps
                ? `(${formatter.time(shift.period.to, true)})`
                : formatter.time(shift.period.to, true)}
            </div>
          </div>
          <div className={scss.label}>
            <h3>{shift.absenceLabel || shift.label}</h3>
            {isAssigningShift && currentEmployee && (
              <div className={scss.originalEmployee}>
                {currentLanguage.OriginalBelongingTo} {currentEmployee.name}
              </div>
            )}
          </div>
        </div>
        {!shiftDraft &&
          !isAssigningShift &&
          (editable ? (
            <Icons.Edit
              color={colorVarMap.colorPrimaryDark}
              onClick={openEditShift}
              ariaLabel={currentLanguage.EditShift}
              className={scss.icon}
              size={IconSize.XSmall}
            />
          ) : (
            !attemptEndAbsence &&
            deleteable && (
              <Icons.Delete
                color={colorVarMap.colorErrorDark}
                onClick={onRemoveShift}
                ariaLabel={currentLanguage.RemoveShift}
                className={scss.icon}
                size={IconSize.XSmall}
              />
            )
          ))}
      </div>
      <ul className={scss.list}>
        {absenceLabel && (
          <ShiftInfoEntry label={currentLanguage.Absence}>
            {absenceLabel}
          </ShiftInfoEntry>
        )}
        {!!shift.responsibilities.length && (
          <ShiftInfoEntry
            label={currentLanguage.responsibility}
            valueClassName={scss.responsibility}
          >
            {shift.responsibilities.map((val, i) => (
              <div key={i}>{val}</div>
            ))}
          </ShiftInfoEntry>
        )}
        {!hideStatus && !isAssigningShift && (
          <ShiftInfoEntry
            label={currentLanguage.Status}
            valueClassName={classNames(
              actionRequired &&
                // Don't highlight "offered" text in red - as there are currently no other actions than cancelling the offer
                shift.status !== TeamShiftStatusEnum.offered &&
                scss.actionRequired
            )}
          >
            {shift.status === TeamShiftStatusEnum.swapActionRequired ? (
              <>
                <Icons.Swap color={colors.colorErrorDark} />
                {currentLanguage.AwaitingApproveSwap}
              </>
            ) : shift.status === TeamShiftStatusEnum.offerActionRequired ? (
              <>
                <Icons.ArrowRight />
                {!shift.numSwapSuggestions
                  ? getShiftStatusText(shift.status)
                  : shift.numSwapSuggestions === 1
                    ? currentLanguage.SingleApplicantFromShiftExchange
                    : currentLanguage.NumApplicantsFromShiftExchange_1(
                        shift.numSwapSuggestions
                      )}
              </>
            ) : shift.status === TeamShiftStatusEnum.actionRequired ? (
              <>
                <Icons.ExclamationFill />
                {getShiftStatusText(shift.status)}
              </>
            ) : (
              getShiftStatusText(shift.status)
            )}
          </ShiftInfoEntry>
        )}

        {shift.deviatingPayerUiLabel && (
          <ShiftInfoEntry
            label={currentLanguage.Location}
            valueClassName={scss.deviatingPayer}
          >
            <Icons.Info size={IconSize.XXXSmall} />
            <span>{shift.deviatingPayerUiLabel}</span>
          </ShiftInfoEntry>
        )}

        {formerAssignee && (
          <ShiftInfoEntry label={currentLanguage.FormerAssignee}>
            {formerAssignee.name}
          </ShiftInfoEntry>
        )}
        {!!substitute?.descCause && (
          <ShiftInfoEntry
            label={currentLanguage.RuleBreak}
            valueClassName={scss.ruleViolation}
          >
            {!!substitute.priority && (
              <div>{Math.floor(substitute.priority)}:&nbsp;</div>
            )}
            <div>{substitute.descCause}</div>
          </ShiftInfoEntry>
        )}
        {shift.activities.length > 0 && (
          <ShiftInfoEntry label={currentLanguage.Activity}>
            {shift.activities.map((activity, index) => (
              <div key={index} className="duty-list">
                <div>{formatActivityOrDutylineTime(activity)}</div>
                <div>{activity.label}</div>
              </div>
            ))}
          </ShiftInfoEntry>
        )}
        {shift.tasks.length > 0 && (
          <ShiftInfoEntry label={currentLanguage.Tasks}>
            {shift.tasks.map((task, index) => (
              <div key={index} className="duty-list">
                <div>{formatActivityOrDutylineTime(task)}</div>
                <div>{task.label}</div>
              </div>
            ))}
          </ShiftInfoEntry>
        )}

        {dutyLines.length > 0 && (
          <ShiftInfoEntry label={currentLanguage.Duty}>
            <Duties dutyLines={dutyLines} />
          </ShiftInfoEntry>
        )}
        {swapDetails?.requestInfo.plannerRemark && (
          <ShiftInfoEntry label={currentLanguage.Note}>
            {swapDetails.requestInfo.plannerRemark}
          </ShiftInfoEntry>
        )}
        {!!offeredTo?.length && (
          <ShiftInfoEntry label={currentLanguage.OfferTo}>
            {offeredTo.map((personGroup, i) => (
              <div key={i}>{personGroup}</div>
            ))}
          </ShiftInfoEntry>
        )}
      </ul>

      {children}
    </div>
  );
}

export default function ShiftCard({
  team,
  shift,
  personId,
  employees,
  absenceTypes,
  currentDate,
  loading,
  callFindSubstitute,
  callAssignShift,
  callMarkAsHandled,
  callRegisterAbsence,
  callCancelOfferOnShiftExchange,
  onRemoveShift,
  setSwapViewOpen,
  setActiveOfferView,
  loadingSwap,
  substitute,
  setEditedShift,
  attemptEndAbsence,
  shiftDef,
  shiftDraft,
  exchangeType,
  dutyLines,
  swapDetails,
}: {
  employees: EmployeeMap;
  team: Team;
  shift: TeamShift;
  personId: number;
  currentDate: Date;
  absenceTypes: AbsenceType[];
  loading: boolean;
  loadingSwap: boolean;
  substitute?: CallIn;
  shiftDef: TeamShiftDef | null;
  shiftDraft: ShiftDraft | null;
  dutyLines: TeamDuty[];
  exchangeType: ExchangeTypeEnum | null;
  swapDetails: Exchange | null;
  callFindSubstitute: (
    teamId: string,
    shiftId: string,
    personId: number
  ) => void;
  callAssignShift: (
    teamId: string,
    personId: number,
    employeeName: string,
    shiftId: string
  ) => void;
  callMarkAsHandled: (
    teamId: string,
    shiftId: string,
    personId: number
  ) => void;
  callRegisterAbsence: (
    teamId: string,
    shiftId: string,
    absenceTypeId: string,
    returnDate: Date | null
  ) => void;
  callCancelOfferOnShiftExchange: (
    teamId: string,
    personId: number,
    shiftId: string
  ) => void;
  onRemoveShift: () => void;
  setSwapViewOpen: (open: boolean) => void;
  setOfferViewOpen: (open: boolean) => void;
  attemptEndAbsence: () => void;
  setEditedShift: (draft: ShiftDraft | null) => void;
  setActiveOfferView: (activeOfferView: ActiveOfferView) => void;
}) {
  const employee = employees[shift.personId];
  const isFictive = !!employee?.isFictive;
  const canRegisterAbsence =
    shouldShowRegisterAbsence(shift) && !isFictive && !substitute;

  const showPlannedWorkCard =
    !shiftRequiresAction(shift) &&
    shift.status !== TeamShiftStatusEnum.absent &&
    shift.status !== TeamShiftStatusEnum.replaced;

  const openEditShift =
    employee && !substitute && shiftDef?.editable
      ? () => {
          setEditedShift(teamShiftToEditedShift(shift, shiftDef, employee));
        }
      : undefined;

  // If the shift type is set, show the actual absence reason; otherwise, mask it
  const maskedAbsence =
    shift.type !== ''
      ? shift.absenceLabel || undefined
      : `(${currentLanguage.Hidden} ${currentLanguage.Cause.toLocaleLowerCase()})`;
  const showAbsenceCard =
    shift.status === TeamShiftStatusEnum.actionRequired ||
    shift.status === TeamShiftStatusEnum.absent ||
    shift.status === TeamShiftStatusEnum.replaced;

  return (
    <>
      {/* This animates and should always be rendered, although it's only relevant when action is required */}
      <ActionRequiredCard
        // Prevent triggering animations when selecting another shift
        key={shift.id}
        callFindSubstitute={callFindSubstitute}
        callMarkAsHandled={callMarkAsHandled}
        isCreatedShift={isCreatedShift(shiftDraft)}
        openOfferView={() => {
          setActiveOfferView(ActiveOfferView.OfferViewList);
        }}
        shift={shift}
        team={team}
        employee={employee}
        isAssigningShift={!!substitute}
        openEditShift={openEditShift}
        cancelOfferOnShiftExchange={() => {
          callCancelOfferOnShiftExchange(team.id, personId, shift.id);
        }}
        exchangeType={exchangeType}
        loadingSwap={loadingSwap}
        openSwapView={() => {
          setSwapViewOpen(true);
        }}
      />
      {(showPlannedWorkCard || substitute) && (
        <ShiftInfo
          dutyLines={dutyLines}
          isCreatedShift={!!shiftDraft}
          shiftDraft={shiftDraft}
          shift={shift}
          isAssigningShift={!!substitute}
          currentEmployee={employee}
          formerAssignee={employees[shift.formerPersonId]}
          onRemoveShift={onRemoveShift}
          substitute={substitute}
          openEditShift={openEditShift}
          testId={ids.ShiftView.plannedWorkCard}
          swapDetails={swapDetails}
        >
          {substitute?.assignable && (
            <Button
              className={scss.assignShift}
              onClick={() => {
                const name = employees[personId]?.name;
                if (name) {
                  callAssignShift(team.id, personId, name, shift.id);
                }
              }}
              id={ids.ShiftView.assign}
            >
              {currentLanguage.assignShift}
            </Button>
          )}
          {canRegisterAbsence && (
            <RegisterAbsenceSection
              key={shift.id}
              onRegisterAbsenceClick={(absenceTypeId, returnDate) => {
                callRegisterAbsence(
                  team.id,
                  shift.id,
                  absenceTypeId,
                  returnDate
                );
              }}
              absenceTypes={absenceTypes}
              shift={shift}
              disabled={
                dateFns.differenceInCalendarDays(
                  shift.period.from,
                  currentDate
                ) < 0
              }
              loading={loading}
              className={scss.registerAbsence}
            />
          )}
          {!substitute &&
            (shift.status === TeamShiftStatusEnum.offered ||
              shift.status === TeamShiftStatusEnum.deputyOffered) && (
              <ShiftActionButton
                id={ids.ShiftView.exchange}
                disabled={shift.status === TeamShiftStatusEnum.deputyOffered}
                onClick={() => {
                  callCancelOfferOnShiftExchange(team.id, personId, shift.id);
                }}
                icon={IconEnum.ArrowRightSlash}
                variant={ShiftActionButtonVariant.Danger}
              >
                {currentLanguage.cancelOfferOnShiftExchange}
              </ShiftActionButton>
            )}
        </ShiftInfo>
      )}
      {showAbsenceCard && (
        <ShiftInfo
          dutyLines={
            isEditedShift(shiftDraft)
              ? shiftDraft.originalShift.dutyLines
              : shift.dutyLines
          }
          shift={shift}
          absenceLabel={maskedAbsence}
          hideStatus={
            (shift.status === TeamShiftStatusEnum.actionRequired ||
              shift.status === TeamShiftStatusEnum.offerActionRequired ||
              shift.status === TeamShiftStatusEnum.offered) &&
            !isCreatedShift(shiftDraft) &&
            !substitute
          }
          attemptEndAbsence={
            shift.dutyLinesOriginal?.length ? attemptEndAbsence : undefined
          }
          shiftDraft={shiftDraft}
          testId={ids.ShiftView.absenceCard}
          swapDetails={null}
        >
          {!!shift.dutyLinesOriginal && (
            <ShiftActionButton
              onClick={attemptEndAbsence}
              icon={IconEnum.Clear}
              variant={ShiftActionButtonVariant.Danger}
            >
              {currentLanguage.EndAbsence}
            </ShiftActionButton>
          )}
        </ShiftInfo>
      )}
    </>
  );
}
